import { useMemo } from 'react';
import { Building2, Monitor, Star } from 'lucide-react';
import { Button } from 'v5/design-system/Button';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { cn } from 'v5/platform/dom/cn';
import {
  FeatureFlagEntry,
  FeatureFlagGroup,
  FeatureFlagValueConfig
} from 'v5/services/front/FeatureManagementService/types';
import { FeatureManagementActionHandler } from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import { buildFeatureFlagCatalogSections } from './buildFeatureFlagCatalog';
import {
  dirtyFeatureIdsForScope,
  entrySupportsScope,
  getConfigForScope,
  scopeKeyToSource,
  type FeatureFlagScopeKey
} from './buildFeatureFlagScopeCatalog';
import {
  bulkSetSourceFeatures,
  bulkSourceFeaturesState
} from './featureFlagCatalogBulkActions';
import {
  FeatureFlagBulkBar,
  FeatureFlagDetailSearch,
  FeatureFlagToggleListBox,
  featureFlagBulkStatus
} from './FeatureFlagInspectorControls';

function StarButton({
  starred,
  featureName,
  onToggle
}: {
  starred: boolean;
  featureName: string;
  onToggle: () => void;
}) {
  return (
    <SimpleTooltip title={starred ? 'Remove from starred' : 'Star this flag'}>
      <Button
        type="button"
        variant="ghost"
        layout="icon"
        size="xs"
        aria-label={starred ? `Unstar ${featureName}` : `Star ${featureName}`}
        aria-pressed={starred}
        className={cn(
          'size-6 shrink-0 text-fg-subtle aria-pressed:border-transparent aria-pressed:bg-transparent',
          starred
            ? 'text-sun opacity-100'
            : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 group-aria-selected:opacity-100'
        )}
        onPointerDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
      >
        <Star className={cn('size-4', starred && 'fill-current')} />
      </Button>
    </SimpleTooltip>
  );
}

function scopeMeta(key: FeatureFlagScopeKey, workspaceName?: string) {
  if (key === 'legacy') {
    return {
      title: 'Legacy',
      isServer: true as const,
      description:
        'Organization-wide server flag. Changing this writes to the backend and affects other people in this organization.',
      icon: (
        <Building2 className="size-5 shrink-0 text-fg-attention" aria-hidden />
      )
    };
  }
  if (key === 'frontend') {
    return {
      title: 'This browser',
      isServer: false as const,
      description:
        'Safe to change — stored in localStorage on this device. Does not affect others. When set, overrides the resolved value for every workspace in this browser.',
      icon: <Monitor className="size-5 shrink-0 text-fg-success" aria-hidden />
    };
  }
  return {
    title: workspaceName ?? 'Workspace',
    isServer: true as const,
    description:
      'Workspace-scoped server flag. Changing this writes to the backend and affects other people in this workspace.',
    icon: null
  };
}

export interface FeatureFlagScopeInspectorProps {
  scopeKey: FeatureFlagScopeKey | null;
  workspaceColumns: Array<{ id: number; name: string }>;
  groups: FeatureFlagGroup[];
  detailSearch: string;
  onDetailSearchChange: (search: string) => void;
  starredFeatureIds: readonly string[];
  onToggleStarred: (featureId: string) => void;
  changedLegacyFlagIds: readonly string[];
  changedFrontendFlagIds: readonly string[];
  changedWorkspaceFlagIds: Readonly<Record<number, readonly string[]>>;
  onAction: FeatureManagementActionHandler;
}

export function FeatureFlagScopeInspector({
  scopeKey,
  workspaceColumns,
  groups,
  detailSearch = '',
  onDetailSearchChange = () => undefined,
  starredFeatureIds,
  onToggleStarred,
  changedLegacyFlagIds,
  changedFrontendFlagIds,
  changedWorkspaceFlagIds,
  onAction
}: FeatureFlagScopeInspectorProps) {
  const starredSet = useMemo(
    () => new Set(starredFeatureIds),
    [starredFeatureIds]
  );

  const dirtyIds = useMemo(() => {
    if (!scopeKey) {
      return new Set<string>();
    }
    return dirtyFeatureIdsForScope(scopeKey, {
      changedLegacyFlagIds,
      changedFrontendFlagIds,
      changedWorkspaceFlagIds
    });
  }, [
    scopeKey,
    changedLegacyFlagIds,
    changedFrontendFlagIds,
    changedWorkspaceFlagIds
  ]);

  const filteredGroups = useMemo(() => {
    if (!scopeKey) {
      return [];
    }
    const query = detailSearch.trim().toLowerCase();
    const sections = buildFeatureFlagCatalogSections(
      groups,
      query,
      starredFeatureIds
    );
    return sections
      .map(group => ({
        ...group,
        entries: group.entries.filter(entry =>
          entrySupportsScope(entry, scopeKey)
        )
      }))
      .filter(group => group.entries.length > 0);
  }, [groups, scopeKey, detailSearch, starredFeatureIds]);

  const allVisibleEntries = useMemo(
    () => filteredGroups.flatMap(group => group.entries),
    [filteredGroups]
  );

  if (!scopeKey) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-8 text-sm text-fg-muted">
        Select a scope to configure flags.
      </div>
    );
  }

  const workspaceName =
    scopeKey.startsWith('workspace-') &&
    workspaceColumns.find(
      column => column.id === Number(scopeKey.slice('workspace-'.length))
    )?.name;
  const meta = scopeMeta(scopeKey, workspaceName || undefined);
  const source = scopeKeyToSource(scopeKey);
  const bulkState = bulkSourceFeaturesState(allVisibleEntries, scopeKey);

  const setAction = (
    entry: FeatureFlagEntry,
    value: FeatureFlagValueConfig<false>['current']
  ) => {
    onAction({
      type: 'setFeatureFlag',
      source,
      featureId: entry.featureId,
      value
    });
  };

  return (
    <ScrollShadowWrapper
      renderTopShadow={false}
      className="min-h-0 min-w-0 flex-1 overflow-y-auto with-styled-scrollbars"
    >
      <div
        className={cn(
          '-header sticky top-0 z-[1] space-y-1 bg-bg-default px-5 py-4',
          '[[data-has-top-shadow]_&]:shadow-sm'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          {meta.icon}
          <h3 className="text-lg font-semibold">{meta.title}</h3>
        </div>
        <p className="text-sm text-fg-muted">{meta.description}</p>
        <FeatureFlagDetailSearch
          value={detailSearch}
          onChange={onDetailSearchChange}
          placeholder="Search flags…"
          aria-label="Search flags in detail"
        />
      </div>
      <div className="flex flex-col gap-4 p-5">
        <section
          className={cn(
            'rounded-lg border p-4',
            meta.isServer
              ? 'border-border-attention bg-bg-attention/40'
              : 'border-border-success bg-bg-success/30'
          )}
        >
          <div className="mb-3 space-y-1">
            <h4
              className={cn(
                'font-semibold mix-blend-multiply',
                meta.isServer ? 'text-fg-attention' : 'text-fg-success'
              )}
            >
              {meta.isServer ? 'Shared on the server' : 'This browser only'}
            </h4>
          </div>
          {filteredGroups.length === 0 ? (
            <p className="rounded-md border border-dashed border-border-muted bg-bg-subtle/40 px-4 py-3 text-sm text-fg-muted">
              {detailSearch.trim()
                ? 'No flags match.'
                : 'No flags available for this scope.'}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredGroups.map(group => (
                <div key={group.name} className="space-y-2">
                  <p className="px-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    {group.name === 'Starred' ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Star className="size-3 fill-current text-sun" />
                        Starred
                      </span>
                    ) : (
                      group.name
                    )}
                  </p>
                  <FeatureFlagToggleListBox
                    ariaLabel={`${group.name} flags`}
                    header={
                      group === filteredGroups[0] && bulkState.canBulk ? (
                        <FeatureFlagBulkBar
                          bulkState={bulkState}
                          statusLabel={featureFlagBulkStatus({
                            ...bulkState,
                            emptyLabel: 'Apply to all flags',
                            unit: 'flag'
                          })}
                          groupAriaLabel="Apply to all flags"
                          onSetAll={enabled =>
                            bulkSetSourceFeatures(
                              allVisibleEntries,
                              scopeKey,
                              enabled,
                              onAction
                            )
                          }
                        />
                      ) : undefined
                    }
                    items={group.entries.map(entry => {
                      const dirty = dirtyIds.has(entry.featureId);
                      const starred = starredSet.has(entry.featureId);
                      const config = getConfigForScope(entry, scopeKey);
                      return {
                        id: entry.featureId,
                        label: entry.featureName,
                        secondary: entry.description || undefined,
                        leadingIcon: dirty ? (
                          <span
                            className="size-2 shrink-0 rounded-full bg-fg-attention"
                            aria-label="Unsaved changes"
                          />
                        ) : (
                          <span className="size-2 shrink-0" aria-hidden />
                        ),
                        config,
                        onChange: value => {
                          if (
                            scopeKey === 'frontend' &&
                            config.supported &&
                            config.optional &&
                            value === false
                          ) {
                            onAction({
                              type: 'clearFeatureFlag',
                              source: 'frontend',
                              featureId: entry.featureId
                            });
                            return;
                          }
                          setAction(entry, value);
                        },
                        extra: (
                          <StarButton
                            starred={starred}
                            featureName={entry.featureName}
                            onToggle={() => onToggleStarred(entry.featureId)}
                          />
                        )
                      };
                    })}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </ScrollShadowWrapper>
  );
}
